import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, MessageCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import { TORONTO_WATERFRONT_IMAGE } from "../lib/brand";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, language: lang });
      setStatus("ok");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus("err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-alabaster" data-testid="contact-page">
      <PageHero
        eyebrow={t("nav.contact")}
        title={t("contact.title")}
        subtitle={t("contact.sub")}
        image={TORONTO_WATERFRONT_IMAGE}
        imageAlt={lang === "fr" ? "Toronto et la Tour CN" : "Toronto and the CN Tower"}
        compact
      />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="section-kicker">{lang === "fr" ? "Nous joindre" : "Contact options"}</p>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight mt-4">
            {lang === "fr" ? "Une question avant de commencer ?" : "A question before you start?"}
          </h2>
          <p className="mt-5 text-navy/65 leading-relaxed max-w-md">
            {lang === "fr"
              ? "Écrivez-nous depuis n’importe quel pays. Les échanges et les appels d’orientation se font entièrement à distance."
              : "Write to us from any country. Messages and orientation calls are handled entirely online."}
          </p>

          <div className="mt-10 space-y-4">
            <a href="mailto:info@jnic.org" className="flex items-center gap-4 rounded-2xl border border-navy/8 bg-white p-4 text-navy hover:border-redcanada/30 hover:-translate-y-0.5 transition" data-testid="contact-email">
              <span className="w-11 h-11 rounded-xl bg-cream text-redcanada grid place-items-center"><Mail size={18} /></span>
              <div><p className="text-xs uppercase tracking-[0.18em] text-navy/40">Email</p><p className="font-medium mt-1">info@jnic.org</p></div>
            </a>
            <a href="https://wa.me/237694056334" target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl border border-navy/8 bg-white p-4 text-navy hover:border-[#25D366]/40 hover:-translate-y-0.5 transition" data-testid="contact-whatsapp">
              <span className="w-11 h-11 rounded-xl bg-[#25D366]/10 text-[#1FB855] grid place-items-center"><MessageCircle size={18} /></span>
              <div><p className="text-xs uppercase tracking-[0.18em] text-navy/40">WhatsApp</p><p className="font-medium mt-1">+237 694 05 63 34</p></div>
            </a>
            <div className="flex items-center gap-4 rounded-2xl border border-navy/8 bg-white p-4 text-navy">
              <span className="w-11 h-11 rounded-xl bg-cream text-gold grid place-items-center"><MapPin size={18} /></span>
              <div><p className="text-xs uppercase tracking-[0.18em] text-navy/40">{lang === "fr" ? "Disponibilité" : "Availability"}</p><p className="font-medium mt-1">Canada · {lang === "fr" ? "service mondial à distance" : "worldwide remote service"}</p></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[2rem] p-7 sm:p-9 lg:p-12 border border-navy/8 shadow-[0_20px_60px_rgba(6,22,42,.07)] space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/45 mb-2 block">{t("contact.name")}</label>
                <input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="form-control" data-testid="contact-name" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/45 mb-2 block">{t("contact.email")}</label>
                <input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="form-control" data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-navy/45 mb-2 block">{t("contact.subject")}</label>
              <input value={form.subject} onChange={(event) => setForm({ ...form, subject: event.target.value })} className="form-control" data-testid="contact-subject" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-navy/45 mb-2 block">{t("contact.message")}</label>
              <textarea required rows={6} value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} className="form-control resize-none" data-testid="contact-message" />
            </div>
            <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-redcanada text-white hover:bg-navy transition text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
              {busy ? t("common.loading") : t("contact.send")} <ArrowRight size={15} />
            </button>
            {status === "ok" && <p className="text-sm text-redcanada text-center" data-testid="contact-success">{t("contact.sent")}</p>}
            {status === "err" && <p className="text-sm text-red-600 text-center">{t("common.error")}</p>}
          </motion.form>
        </div>
      </section>
    </div>
  );
}
